import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";

const TAX_RATE_OPTIONS = [
  { value: "0", labelKey: "productModal.taxRateOptions.zero" },
  { value: "5", labelKey: "productModal.taxRateOptions.five" },
  { value: "15", labelKey: "productModal.taxRateOptions.fifteen" },
];

const EMPTY_FORM = {
  name: "",
  description: "",
  unitSellingPrice: "",
  taxRate: "15",
  vatCategoryCode: "S",
  exemptionCategory: "",
  exemptionReasonCode: "",
  exemptionReason: "",
  customsCode: "",
  uniqueCode: "",
};

const EXEMPTION_REASONS = [
  { value: "", labelKey: "productModal.exemptionCategories.select" },
  {
    value: "zero_rated",
    labelKey: "productModal.exemptionCategories.zeroRated",
  },
  {
    value: "exempt_supply",
    labelKey: "productModal.exemptionCategories.exemptSupply",
  },
  {
    value: "out_of_scope",
    labelKey: "productModal.exemptionCategories.outOfScope",
  },
];

const SPECIFIC_EXEMPTION_REASONS = {
  zero_rated: [
    { value: "", labelKey: "productModal.exemptionReasons.select" },
    {
      value: "VATEX-SA-32",
      labelKey: "productModal.exemptionReasons.exportGoods",
      reasonText: "Export of goods",
    },
    {
      value: "VATEX-SA-33",
      labelKey: "productModal.exemptionReasons.exportServices",
      reasonText: "Export of services",
    },
    {
      value: "VATEX-SA-35",
      labelKey: "productModal.exemptionReasons.medicines",
      reasonText: "Medicines and medical equipment",
    },
    {
      value: "VATEX-SA-36",
      labelKey: "productModal.exemptionReasons.qualifyingMetals",
      reasonText: "Qualifying metals",
    },
  ],
  exempt_supply: [
    { value: "", labelKey: "productModal.exemptionReasons.select" },
    {
      value: "VATEX-SA-29",
      labelKey: "productModal.exemptionReasons.financialServices",
      reasonText: "Financial services",
    },
    {
      value: "VATEX-SA-30",
      labelKey: "productModal.exemptionReasons.realEstate",
      reasonText: "Real estate transactions",
    },
    {
      value: "VATEX-SA-29-7",
      labelKey: "productModal.exemptionReasons.lifeInsurance",
      reasonText: "Life insurance services",
    },
  ],
  out_of_scope: [
    { value: "", labelKey: "productModal.exemptionReasons.select" },
    {
      value: "VATEX-SA-OOS",
      labelKey: "productModal.exemptionReasons.outOfScopeVat",
      reasonText: "Out of scope of VAT",
    },
  ],
};

const AddProductModal = ({
  existingProduct,
  onSave,
  onClose,
  isSaving = false,
}) => {
  const { t, i18n } = useTranslation("pages");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const isEdit = !!existingProduct;
  const locale = i18n.language === "ar" ? "ar-SA" : "en-SA";

  useEffect(() => {
    if (existingProduct) {
      setForm({
        name: existingProduct.name || "",
        description: existingProduct.description || "",
        unitSellingPrice: existingProduct.unitSellingPrice?.toString() || "",
        taxRate: existingProduct.taxRate?.toString() || "15",
        vatCategoryCode: existingProduct.vatCategoryCode || "S",
        exemptionCategory: existingProduct.exemptionCategory || "",
        exemptionReasonCode: existingProduct.exemptionReasonCode || "",
        exemptionReason: existingProduct.exemptionReason || "",
        customsCode: existingProduct.customsCode || "",
        uniqueCode: existingProduct.uniqueCode || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [existingProduct]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = t("productModal.validation.nameRequired");

    if (!form.unitSellingPrice && form.unitSellingPrice !== 0) {
      e.unitSellingPrice = t("productModal.validation.unitPriceRequired");
    } else if (
      isNaN(parseFloat(form.unitSellingPrice)) ||
      parseFloat(form.unitSellingPrice) < 0
    ) {
      e.unitSellingPrice = t("productModal.validation.unitPriceInvalid");
    }

    if (form.taxRate === "" || form.taxRate === null) {
      e.taxRate = t("productModal.validation.taxRateRequired");
    }

    const taxRate = Number(form.taxRate || 0);

    if (taxRate === 0) {
      if (!form.vatCategoryCode) {
        e.vatCategoryCode = t(
          "productModal.validation.vatCategoryRequired",
        );
      }

      if (!form.exemptionCategory) {
        e.exemptionCategory = t(
          "productModal.validation.exemptionCategoryRequired",
        );
      }

      if (!form.exemptionReasonCode) {
        e.exemptionReasonCode = t(
          "productModal.validation.exemptionReasonCodeRequired",
        );
      }

      if (!form.exemptionReason) {
        e.exemptionReason = t(
          "productModal.validation.exemptionReasonRequired",
        );
      }
    }

    return e;
  };

  const handleTaxRateChange = (value) => {
    set("taxRate", value);

    if (Number(value) > 0) {
      setForm((prev) => ({
        ...prev,
        taxRate: value,
        vatCategoryCode: "S",
        exemptionCategory: "",
        exemptionReasonCode: "",
        exemptionReason: "",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        taxRate: value,
        vatCategoryCode: "",
        exemptionCategory: "",
        exemptionReasonCode: "",
        exemptionReason: "",
      }));
    }
  };

  const handleExemptionCategoryChange = (value) => {
    const vatCategoryCode =
      value === "zero_rated"
        ? "Z"
        : value === "exempt_supply"
          ? "E"
          : value === "out_of_scope"
            ? "O"
            : "";

    setForm((prev) => ({
      ...prev,
      exemptionCategory: value,
      vatCategoryCode,
      exemptionReasonCode: "",
      exemptionReason: "",
    }));

    setErrors((prev) => ({
      ...prev,
      exemptionCategory: "",
      vatCategoryCode: "",
      exemptionReasonCode: "",
      exemptionReason: "",
    }));
  };

  const handleExemptionReasonChange = (value) => {
    const reason = SPECIFIC_EXEMPTION_REASONS[form.exemptionCategory]?.find(
      (item) => item.value === value,
    );

    setForm((prev) => ({
      ...prev,
      exemptionReasonCode: value,
      exemptionReason: reason?.reasonText || "",
    }));

    setErrors((prev) => ({
      ...prev,
      exemptionReasonCode: "",
      exemptionReason: "",
    }));
  };

  const handleSave = () => {
    const e = validate();

    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    const selectedReason = SPECIFIC_EXEMPTION_REASONS[
      form.exemptionCategory
    ]?.find((reason) => reason.value === form.exemptionReasonCode);

    onSave({
      ...(isEdit ? { id: existingProduct.id } : {}),
      name: form.name.trim(),
      description: form.description.trim(),
      unitSellingPrice: parseFloat(form.unitSellingPrice),
      taxRate: parseFloat(form.taxRate),
      vatCategoryCode:
        Number(form.taxRate || 0) > 0 ? "S" : form.vatCategoryCode,
      exemptionCategory:
        Number(form.taxRate || 0) > 0 ? "" : form.exemptionCategory,
      exemptionReasonCode:
        Number(form.taxRate || 0) > 0 ? "" : form.exemptionReasonCode,
      exemptionReason:
        Number(form.taxRate || 0) > 0
          ? ""
          : selectedReason?.reasonText || form.exemptionReason,
      customsCode: form.customsCode.trim(),
      uniqueCode: form.uniqueCode.trim(),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 16 }}
        transition={{ duration: 0.22 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit
                  ? t("productModal.titleEdit")
                  : t("productModal.titleAdd")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEdit
                  ? t("productModal.subtitleEdit")
                  : t("productModal.subtitleAdd")}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("productModal.fields.name")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("productModal.placeholders.name")}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("productModal.fields.description")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder={t("productModal.placeholders.description")}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
          </div>

          {/* Unit Selling Price + Tax Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("productModal.fields.unitSellingPrice")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                  SR
                </span>
                <Input
                  type="number"
                  value={form.unitSellingPrice}
                  onChange={(e) => set("unitSellingPrice", e.target.value)}
                  placeholder={t("productModal.placeholders.price")}
                  min="0"
                  step="0.01"
                  className={`ps-9 ${
                    errors.unitSellingPrice ? "border-red-500" : ""
                  }`}
                />
              </div>
              {errors.unitSellingPrice && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.unitSellingPrice}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("productModal.fields.taxRate")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-1.5">
                {TAX_RATE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleTaxRateChange(opt.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-start text-xs transition-all duration-150 ${
                      form.taxRate === opt.value
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    <div
                      className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                        form.taxRate === opt.value
                          ? "border-primary bg-primary"
                          : "border-muted-foreground"
                      }`}
                    />
                    {t(opt.labelKey)}
                  </button>
                ))}
              </div>

              {/* Custom tax rate input */}
              {!TAX_RATE_OPTIONS.map((o) => o.value).includes(form.taxRate) && (
                <Input
                  type="number"
                  value={form.taxRate}
                  onChange={(e) => handleTaxRateChange(e.target.value)}
                  placeholder={t("productModal.placeholders.customRate")}
                  min="0"
                  max="100"
                  step="0.5"
                  className="mt-2 text-sm"
                />
              )}

              <button
                type="button"
                onClick={() => handleTaxRateChange("")}
                className="mt-1.5 text-xs text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
              >
                {t("productModal.actions.enterCustomRate")}
              </button>

              {errors.taxRate && (
                <p className="text-xs text-red-500 mt-1">{errors.taxRate}</p>
              )}
            </div>
          </div>

          {Number(form.taxRate) === 0 && (
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t("productModal.exemption.title")}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("productModal.exemption.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t("productModal.fields.exemptionCategory")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.exemptionCategory}
                    onChange={(e) =>
                      handleExemptionCategoryChange(e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.exemptionCategory
                        ? "border-red-500"
                        : "border-border"
                    }`}
                  >
                    {EXEMPTION_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {t(reason.labelKey)}
                      </option>
                    ))}
                  </select>
                  {errors.exemptionCategory && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.exemptionCategory}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t("productModal.fields.vatCategory")}
                  </label>
                  <Input
                    type="text"
                    value={form.vatCategoryCode}
                    readOnly
                    placeholder={t("productModal.placeholders.autoSelected")}
                    className={errors.vatCategoryCode ? "border-red-500" : ""}
                  />
                  {errors.vatCategoryCode && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.vatCategoryCode}
                    </p>
                  )}
                </div>
              </div>

              {form.exemptionCategory && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t("productModal.fields.specificExemptionReason")}
                    <span className="text-red-500"> *</span>
                  </label>
                  <select
                    value={form.exemptionReasonCode}
                    onChange={(e) =>
                      handleExemptionReasonChange(e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.exemptionReasonCode
                        ? "border-red-500"
                        : "border-border"
                    }`}
                  >
                    {(
                      SPECIFIC_EXEMPTION_REASONS[form.exemptionCategory] || []
                    ).map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {t(reason.labelKey)}
                      </option>
                    ))}
                  </select>
                  {errors.exemptionReasonCode && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.exemptionReasonCode}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customs Code + Unique Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("productModal.fields.customsCode")}
              </label>
              <Input
                type="text"
                value={form.customsCode}
                onChange={(e) => set("customsCode", e.target.value)}
                placeholder={t("productModal.placeholders.customsCode")}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("productModal.helpers.customsCode")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("productModal.fields.uniqueCode")}
              </label>
              <Input
                type="text"
                value={form.uniqueCode}
                onChange={(e) => set("uniqueCode", e.target.value)}
                placeholder={t("productModal.placeholders.uniqueCode")}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("productModal.helpers.uniqueCode")}
              </p>
            </div>
          </div>

          {/* ZATCA notice */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon
                name="Info"
                size={18}
                className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
              />
              <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                {t("productModal.notice")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between flex-shrink-0">
          {/* Live price preview */}
          {form.unitSellingPrice && parseFloat(form.unitSellingPrice) > 0 && (
            <div className="text-sm text-muted-foreground">
              {t("productModal.totalWithTax")}{" "}
              <span className="font-semibold text-foreground">
                SR{" "}
                {(
                  parseFloat(form.unitSellingPrice) *
                  (1 + parseFloat(form.taxRate || 0) / 100)
                ).toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          )}
          {!form.unitSellingPrice && <div />}

          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t("common:actions.close")}
            </Button>
            <Button
              variant="default"
              size="md"
              iconName={isEdit ? "Save" : "Package"}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving
                ? t("productModal.actions.saving")
                : isEdit
                  ? t("productModal.actions.saveChanges")
                  : t("productModal.actions.saveAndExit")}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddProductModal;
